import { formatDateTime } from "@clipboard-health/date-time";
import { CbhIcon, Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack } from "@mui/material";
import { type ClipboardScoreLog } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreHistory";
import { parseISO } from "date-fns";
import { isEmpty } from "lodash";
import { type ReactNode } from "react";

import { ClipboardScoreActions, reasonDisplayMap } from "../../utils/constants";
import { getActionLabel, getConsequence, getConsequenceType } from "../../utils/utils";
import { ShortLeadTimeShiftInfo } from "../Adjustments/ShortLeadTimeShiftInfo";
import { type Consequence } from "./ConsequencePill";
import { ActivityItem } from "./Item";

export interface ActivityItemForLogProps {
  id: string;
  log: ClipboardScoreLog;
  isDynamicPenaltiesEnabled: boolean;
  showDetails: boolean;
  expanded?: boolean;
  useExpectedArrivalTime?: boolean;
  onClick?: (id: string, isExpanded: boolean) => void;
}

interface ItemDetails {
  label: string;
  value: string | ReactNode;
}

interface LogDetails {
  itemDetails: ItemDetails[];
  callout?: React.ReactNode;
}

interface GetCaptionProps {
  action: string;
  reason?: string;
  consequence?: Consequence;
  multiplier?: number;
}

interface GetCalloutProps {
  action: string;
  showShortLeadTimeInfo?: boolean;
}

function getCallout({ action, showShortLeadTimeInfo }: GetCalloutProps) {
  if (action === ClipboardScoreActions.START_SHIFT_LATE && showShortLeadTimeInfo) {
    return <ShortLeadTimeShiftInfo />;
  }

  return undefined;
}

function getCaption({ action, consequence, reason, multiplier }: GetCaptionProps) {
  if (consequence === "On Restriction") {
    return (
      <Text variant="body2" color={(theme) => theme.palette.grey[600]}>
        You were already Restricted, so your score didn&apos;t change.
      </Text>
    );
  }

  if (consequence === 0 && isDefined(reason) && !isEmpty(reasonDisplayMap[action][reason])) {
    return (
      <Text variant="body2" color={(theme) => theme.palette.grey[600]}>
        {reasonDisplayMap[action][reason]}
      </Text>
    );
  }

  if (isDefined(multiplier) && multiplier > 1) {
    if (action === ClipboardScoreActions.START_SHIFT_LATE) {
      return (
        <Text variant="body2" color={(theme) => theme.palette.intent?.error.text}>
          This deduction was higher due to recent late arrivals.
        </Text>
      );
    }

    if (action === ClipboardScoreActions.CANCEL_SHIFT) {
      return (
        <Text variant="body2" color={(theme) => theme.palette.intent?.error.text}>
          This deduction was higher due to recent same-day cancels.
        </Text>
      );
    }
  }

  return null;
}

interface GetLogDetailsProps {
  log: ClipboardScoreLog;
  consequence?: Consequence;
  showShortLeadTimeInfo?: boolean;
}

function getLogDetails({
  log,
  consequence,
  showShortLeadTimeInfo,
}: GetLogDetailsProps): LogDetails {
  const itemDetails: ItemDetails[] = [];

  if (isDefined(log.facilityName)) {
    itemDetails.push({
      label: "Workplace",
      value: log.facilityName,
    });
  }

  // we prefer target arrival time over shift start time
  // only fall back to shift start time if target arrival time is not defined
  if (isDefined(log.targetArrivalTime)) {
    itemDetails.push({
      label: "Target arrival time",
      value: formatDateTime(parseISO(log.targetArrivalTime)),
    });
  } else if (isDefined(log.shiftStart)) {
    itemDetails.push({
      label: "Shift start time",
      value: formatDateTime(parseISO(log.shiftStart)),
    });
  }

  if (isDefined(log.clockInTime)) {
    itemDetails.push({
      label: "Clocked in at",
      value: formatDateTime(parseISO(log.clockInTime)),
    });
  } else {
    itemDetails.push({
      label: log.action === ClipboardScoreActions.CANCEL_SHIFT ? "Cancelled at" : "Recorded at",
      value: formatDateTime(parseISO(log.createdAt)),
    });
  }

  if (
    isDefined(log.clipboardScoreUpdatedScore) &&
    isDefined(log.clipboardScoreAdjustment) &&
    log.clipboardScoreAdjustment !== 0 &&
    consequence !== "On Restriction"
  ) {
    itemDetails.push({
      label: "Score before / after",
      value: (
        <Stack direction="row" spacing={2} alignItems="center">
          <Text inline variant="body2">
            {log.clipboardScoreUpdatedScore - log.clipboardScoreAdjustment} points
          </Text>
          <CbhIcon type="arrow-right" size="xSmall" />
          <Text inline variant="body2">
            {log.clipboardScoreUpdatedScore} points
          </Text>
        </Stack>
      ),
    });
  }

  return {
    itemDetails,
    callout: getCallout({ action: log.action, showShortLeadTimeInfo }),
  };
}

export function ActivityItemForLog({
  id,
  log,
  isDynamicPenaltiesEnabled,
  showDetails,
  expanded,
  onClick,
  useExpectedArrivalTime,
}: ActivityItemForLogProps) {
  const label = getActionLabel(log);
  const consequence = getConsequence({
    action: log.action,
    score: log.clipboardScoreUpdatedScore ?? 0,
    adjustment: log.clipboardScoreAdjustment ?? 0,
  });
  const consequenceType = getConsequenceType(log.action);
  const multiplier = isDynamicPenaltiesEnabled ? Number(log.clipboardScoreMultiplier) : undefined;
  const caption = getCaption({
    action: log.action,
    consequence,
    multiplier,
    ...(isDefined(log.reason) && { reason: log.reason }),
  });
  const details = getLogDetails({
    log,
    consequence,
    showShortLeadTimeInfo: useExpectedArrivalTime && isDefined(log.targetArrivalTime),
  });

  const handleChange = () => {
    if (isDefined(onClick) && isDefined(expanded)) {
      onClick(id, expanded);
    }
  };

  return (
    <ActivityItem
      label={label}
      consequence={consequence}
      consequenceType={consequenceType}
      multiplier={multiplier}
      caption={caption}
      expanded={expanded}
      setExpanded={handleChange}
    >
      {showDetails && (
        <Stack padding={2} paddingLeft={6}>
          <Stack spacing={2}>
            {details.itemDetails.map((itemDetail) => (
              <Stack
                key={itemDetail.label}
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Text variant="body2">{itemDetail.label}</Text>
                <Text variant="body2">{itemDetail.value}</Text>
              </Stack>
            ))}

            {isDefined(details.callout) && <Box paddingTop={3}>{details.callout}</Box>}
          </Stack>
        </Stack>
      )}
    </ActivityItem>
  );
}
